import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
];

interface LanguageSwitcherProps {
  dropUp?: boolean;
}

const LanguageSwitcher = ({ dropUp = false }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-[6px] px-[12px] py-[8px] rounded-[8px] bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.1] transition-all duration-200 text-white text-[14px] font-semibold"
      >
        <span className="text-[16px]">{currentLang.flag}</span>
        <span>{currentLang.label}</span>
        <svg
          className={`w-[12px] h-[12px] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute right-0 z-50 min-w-[120px] rounded-[10px] bg-[#1a2235] border border-white/[0.1] shadow-[0_8px_30px_rgba(0,0,0,0.4)] overflow-hidden ${dropUp ? 'bottom-[calc(100%+4px)]' : 'top-[calc(100%+4px)]'}`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-[8px] px-[14px] py-[10px] text-[14px] font-medium transition-colors duration-150 ${
                lang.code === i18n.language
                  ? 'bg-[#0086D3]/20 text-[#4DBDFF]'
                  : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
              }`}
            >
              <span className="text-[16px]">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
